import React from 'react';
import { Button, Card } from 'react-bootstrap';
import {
  BloodworkAnalysis,
  Section,
  practitionerSectionStateKey,
  practitionerSectionTitle,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { SECTION_HEADINGS } from './supplements-summary-panel';
import {
  renderNoteBody,
  MarkdownToolbar,
  wrapSelection,
  formatMarkerForKeydown,
} from './note-markdown';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import { isSameBloodwork } from '../../utils/sameBloodwork';
import { estimateNoteCardHeight } from '../../helpers/pdfPanelMetrics';

// This card's own chrome (title row + card padding) and body line height, in
// capture px. The Insights card that follows it in the PDF carries its own
// pair; everything else about the two estimates is shared.
const SECTION_NOTES_CARD_OVERHEAD = 100;
const SECTION_NOTES_LINE_HEIGHT = 22;

// The consolidated "Section Notes" panel gathers each body-system section's own
// Notes-tab text into a single panel near the report bottom, mirroring how
// SupplementsSummaryPanel consolidates the per-section Supplements tabs. The
// per-section note under each section's bar graphs (pdf-section.tsx) is removed
// in favour of this panel so long notes stop competing with the bar graphs for
// space on the page.
//
// Storage:
//   - Auto-generated body reads each section's own note (bloodworkNotes keyed
//     by that section) in canonical SECTION_HEADINGS order.
//   - An optional practitioner edit is stored as ONE override note keyed by the
//     Section.SECTION_NOTES pseudo-section (same override pattern as Supplements
//     with Section.SUPPLEMENTS). Reset clears it back to the auto-generated body.

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  forPdf?: boolean;
  includeInPdf?: boolean;
  onIncludeInPdfToggle?: () => void;
  // Required for Edit-as-override. When absent, the Edit button is hidden.
  bloodworkEntryStore?: BloodworkEntryStore;
};

type State = {
  editing: boolean;
  draftText: string;
};

type NoteBlock = { section: Section; label: string; text: string };

// Walk the canonical section order and pull each section's own note text,
// skipping sections the practitioner left blank.
function collectSectionNotes(bloodworkAnalysis: BloodworkAnalysis): NoteBlock[] {
  const notes = bloodworkAnalysis?.bloodwork?.bloodworkNotes;
  if (!notes) return [];
  const blocks: NoteBlock[] = [];
  SECTION_HEADINGS.forEach(({ section, label }) => {
    const note = notes.findNote(section);
    const text = (note?.text ?? '').trim();
    if (text) blocks.push({ section, label, text });
  });
  // Then the sections the practitioner named, in the order they appear on the
  // report. Their notes are keyed by practitionerSectionStateKey rather than by
  // a Section value — every one of them renders as Section.CUSTOM_MARKERS, so a
  // shared key would give them all one note between them.
  (bloodworkAnalysis?.getPractitionerSectionNames?.() || []).forEach(name => {
    const text = (
      notes.findNote(practitionerSectionStateKey(name) as Section)?.text ?? ''
    ).trim();
    if (text) {
      blocks.push({
        section: Section.CUSTOM_MARKERS,
        label: practitionerSectionTitle(name),
        text,
      });
    }
  });
  // The practitioner's own markers, last — SECTION_HEADINGS covers the shipped
  // body systems only, and without this a note written on that section's Notes
  // tab would save but never reach this panel or the PDF. Its heading is the
  // section name itself.
  const customNote = (notes.findNote(Section.CUSTOM_MARKERS)?.text ?? '').trim();
  if (customNote) {
    blocks.push({
      section: Section.CUSTOM_MARKERS,
      label: Section.CUSTOM_MARKERS,
      text: customNote,
    });
  }
  return blocks;
}

// Lightweight convention shared by the auto body and the editable override:
//   "## Label"     → section heading (h6)
//   anything else   → body text (whitespace preserved via pre-wrap)
function blocksToText(blocks: NoteBlock[]): string {
  return blocks.map(b => `## ${b.label}\n${b.text}`).join('\n\n');
}

function readOverride(bloodworkAnalysis: BloodworkAnalysis): string {
  const note = bloodworkAnalysis?.bloodwork?.bloodworkNotes?.findNote(
    Section.SECTION_NOTES,
  );
  return (note?.text ?? '').trimStart();
}

// The text actually shown: the practitioner's edited override when present,
// otherwise the auto-generated body. Exposed statically so the PDF container
// can decide whether there is anything to render / reserve space for.
function bodyTextFor(bloodworkAnalysis: BloodworkAnalysis): string {
  const override = readOverride(bloodworkAnalysis);
  if (override) return override;
  return blocksToText(collectSectionNotes(bloodworkAnalysis));
}

// Heading + inline-marker rendering lives in note-markdown (renderNoteBody), so
// this panel, the per-section Notes tab and the Insights panel all draw a saved
// note identically.

export class SectionNotesSummaryPanel extends React.Component<Props, State> {
  state: State = {
    editing: false,
    draftText: '',
  };

  private editTextareaEl: HTMLTextAreaElement | null = null;

  // A draft belongs to the test it was opened on. The analysis screen keeps this
  // panel mounted and swaps the test under it when the practitioner analyzes
  // another row, so an edit left open on the previous test would otherwise be
  // saved over the new one's. See isSameBloodwork.
  componentWillReceiveProps(nextProps: Props) {
    if (
      !isSameBloodwork(
        nextProps.bloodworkAnalysis?.bloodwork,
        this.props.bloodworkAnalysis?.bloodwork,
      )
    ) {
      this.setState({ editing: false, draftText: '' });
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.editing && !prevState.editing) {
      this.fitTextareaToContent();
    }
  }

  private setEditTextareaRef = (el: HTMLTextAreaElement | null) => {
    this.editTextareaEl = el;
  };

  private fitTextareaToContent = () => {
    const el = this.editTextareaEl;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = 'hidden';
  };

  // Inline-markdown formatting for the override editor (Bold/Italic/Underline).
  private applyFormat = (value: string, selStart: number, selEnd: number) => {
    this.setState({ draftText: value }, () => {
      this.fitTextareaToContent();
      const el = this.editTextareaEl;
      if (el) {
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(selStart, selEnd);
        });
      }
    });
  };

  private handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const marker = formatMarkerForKeydown(e);
    if (marker && this.editTextareaEl) {
      e.preventDefault();
      const { value, selStart, selEnd } = wrapSelection(this.editTextareaEl, marker);
      this.applyFormat(value, selStart, selEnd);
    }
  };

  // The consolidated body text (override or auto). Static so the PDF container
  // can gate rendering on `bodyText(...).trim()` without instantiating.
  static bodyText(bloodworkAnalysis: BloodworkAnalysis): string {
    if (!bloodworkAnalysis?.bloodwork) return '';
    return bodyTextFor(bloodworkAnalysis);
  }

  // The auto-generated body alone, ignoring any override. Static so the
  // comparison report's Insights & Section Notes table can offer the same edit
  // without duplicating the "## Label" assembly.
  static autoText(bloodworkAnalysis: BloodworkAnalysis): string {
    if (!bloodworkAnalysis?.bloodwork) return '';
    return blocksToText(collectSectionNotes(bloodworkAnalysis));
  }

  /**
   * Store an edited Section Notes body on a test.
   *
   * Text matching the auto-generated body is stored as an EMPTY override, not
   * as itself: opening the editor and saving out of habit must not freeze
   * today's aggregate in place and quietly stop the panel following each
   * section's notes from then on. Callers save the bloodwork themselves.
   */
  static writeOverrideOn(
    bloodworkAnalysis: BloodworkAnalysis,
    text: string,
  ): void {
    const notes = bloodworkAnalysis?.bloodwork?.bloodworkNotes;
    if (!notes) return;
    const auto = SectionNotesSummaryPanel.autoText(bloodworkAnalysis);
    const next = (text || '').trim() === auto.trim() ? '' : text;
    let note = notes.findNote(Section.SECTION_NOTES);
    if (!note) note = notes.addNote(Section.SECTION_NOTES, next);
    note.text = next;
  }

  /** Whether this test carries an edited override rather than the auto body. */
  static hasOverride(bloodworkAnalysis: BloodworkAnalysis): boolean {
    return !!readOverride(bloodworkAnalysis).trim();
  }

  // Rough height (px, html2pdf canvas units) of the rendered PDF panel, used by
  // the container to decide page breaks below the last bar-graph section. Errs
  // high — overestimating just pushes following content to a fresh page.
  static estimatedPdfHeight(bloodworkAnalysis: BloodworkAnalysis): number {
    // The wrapped-row arithmetic itself is shared with the Insights card below
    // this one in the PDF — see estimateNoteCardHeight. Only the card's own
    // chrome and body type are local.
    return estimateNoteCardHeight(
      SectionNotesSummaryPanel.bodyText(bloodworkAnalysis),
      { cardOverhead: SECTION_NOTES_CARD_OVERHEAD, lineHeight: SECTION_NOTES_LINE_HEIGHT },
    );
  }

  private handleEditClick = () => {
    this.setState({
      editing: true,
      draftText: SectionNotesSummaryPanel.bodyText(this.props.bloodworkAnalysis),
    });
  };

  private handleCancel = () => {
    this.setState({ editing: false, draftText: '' });
  };

  private writeNote = (text: string) => {
    const notes = this.props.bloodworkAnalysis.bloodwork.bloodworkNotes;
    let note = notes.findNote(Section.SECTION_NOTES);
    if (!note) note = notes.addNote(Section.SECTION_NOTES, text);
    note.text = text;
  };

  // Saving flips a tall textarea back to a shorter view WHILE the app's spinner
  // modal is up — and that modal locks body scrolling, so restoring a captured
  // scrollY underneath it was clamped to 0 and left the report at the very top.
  // Land back at the top of this panel instead, once the save has resolved.
  private scrollBackToPanel = () => {
    scrollPanelIntoView('section-notes');
  };

  private handleSave = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    // If the practitioner didn't actually change anything (opened Edit and hit
    // Save out of habit), DON'T freeze the current aggregate into a static
    // override — store an empty override so the panel keeps auto-following each
    // section's notes. Only a genuine edit persists as an override.
    SectionNotesSummaryPanel.writeOverrideOn(
      bloodworkAnalysis,
      this.state.draftText,
    );
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(this.scrollBackToPanel)
      .catch(err => {
        console.error('Failed to save Section Notes override', err);
        alert('Failed to save your changes. Please try again.');
        this.scrollBackToPanel();
      });
    this.setState({ editing: false, draftText: '' });
  };

  private handleReset = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    if (
      !window.confirm(
        'Clear your edits and rebuild Section Notes from each section’s notes?',
      )
    ) {
      return;
    }
    this.writeNote('');
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(this.scrollBackToPanel)
      .catch(err => {
        console.error('Failed to reset Section Notes override', err);
        alert('Failed to reset. Please try again.');
        this.scrollBackToPanel();
      });
    this.setState({ editing: false, draftText: '' });
  };

  render() {
    const {
      bloodworkAnalysis,
      forPdf,
      includeInPdf,
      onIncludeInPdfToggle,
      bloodworkEntryStore,
    } = this.props;
    const { editing, draftText } = this.state;

    if (!bloodworkAnalysis?.bloodwork) {
      return null;
    }

    const override = readOverride(bloodworkAnalysis);
    const source = override || blocksToText(collectSectionNotes(bloodworkAnalysis));

    // Nothing typed in any section and no override → render nothing at all,
    // exactly like the Supplements panel when there are no supplements.
    if (!source.trim()) {
      return null;
    }

    const body = renderNoteBody(source);

    if (forPdf) {
      // NOTE: deliberately NOT "disclaimer-panel" — that class is in html2pdf's
      // page-break-inside:avoid list, which would stop this (potentially long,
      // multi-section) card from paginating. A plain Card looks identical but
      // can break across pages; the Card.Header keeps `page-break-after: avoid`
      // (.pdf-page .card-header) so it never orphans, and each ## label carries
      // its own avoid via HEADING_STYLE.
      // No bottom margin either: the space above the Insights card that
      // follows is owned by that card's own top pad (bloodwork-analysis-pdf),
      // so the three trailing cards all sit the same distance below whatever
      // is above them. A trailing margin here would stack on that pad, and
      // would spill across a page boundary when this card ends near one.
      return (
        <Card style={{ whiteSpace: 'pre-wrap' }}>
          <Card.Header className="py-1">Section Notes</Card.Header>
          <Card.Body
            className="disclaimer-body note-card-body"
            style={{ paddingBottom: 12 }}
          >
            {body}
          </Card.Body>
        </Card>
      );
    }

    const viewBody = (
      <>
        <div
          style={{
            border: '1px solid #ced4da',
            borderRadius: 4,
            padding: 12,
            backgroundColor: '#fff',
          }}
        >
          {body}
        </div>
        {bloodworkEntryStore && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <Button
              type="button"
              variant="primary"
              title="Edit Section Notes"
              onClick={this.handleEditClick}
            >
              Edit
            </Button>
            {override && (
              <Button
                type="button"
                variant="outline-danger"
                title="Discard your edits and rebuild from each section&rsquo;s notes"
                onClick={this.handleReset}
              >
                Reset to default
              </Button>
            )}
          </div>
        )}
      </>
    );

    const editBody = (
      <>
        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 6 }}>
          Formatting:&nbsp;<code>## Section</code>&nbsp;for section headings,&nbsp;
          <code>**bold**</code>,&nbsp;<code>*italic*</code>,&nbsp;<code>_underline_</code>.
        </div>
        <MarkdownToolbar
          getTextarea={() => this.editTextareaEl}
          onApply={this.applyFormat}
        />
        <textarea
          ref={this.setEditTextareaRef}
          value={draftText}
          onChange={e =>
            this.setState({ draftText: e.target.value }, this.fitTextareaToContent)
          }
          onKeyDown={this.handleEditKeyDown}
          rows={18}
          style={{
            width: '100%',
            // A textarea inherits neither the page font nor its size, so both
            // are set explicitly — what's typed has to be the same size as the
            // preview it turns into (the app's body text is 12px, which is what
            // Bootstrap's .form-control uses for every other note field).
            fontFamily: 'inherit',
            fontSize: '12px',
            padding: 8,
            borderRadius: 4,
            border: '1px solid #ced4da',
          }}
        />
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={this.handleSave}>
            Save
          </Button>
          <Button variant="secondary" onClick={this.handleCancel}>
            Cancel
          </Button>
          {override && (
            <Button
              variant="outline-danger"
              onClick={this.handleReset}
              style={{ marginLeft: 'auto' }}
              title="Discard your edits and rebuild from each section&rsquo;s notes"
            >
              Reset to default
            </Button>
          )}
        </div>
      </>
    );

    return (
      <Card id="section-notes">
        <Card.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Section Notes</span>
          {!editing && onIncludeInPdfToggle && (
            <label
              htmlFor="include-in-pdf-section-notes"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                margin: 0,
                fontWeight: 'normal',
                cursor: 'pointer',
                color: '#fff',
              }}
              title="Include Section Notes in the Analysis PDF (when exporting with notes)"
            >
              <span style={{ marginRight: '6px' }}>Include in PDF</span>
              <input
                id="include-in-pdf-section-notes"
                type="checkbox"
                className="lsm-styled-checkbox"
                checked={!!includeInPdf}
                onChange={onIncludeInPdfToggle}
              />
            </label>
          )}
        </Card.Header>
        <Card.Body>{editing ? editBody : viewBody}</Card.Body>
      </Card>
    );
  }
}
